/**
 * DrawingApp.tsx
 * 
 * 职责：
 * 1. 作为应用程序的主入口点和根组件
 * 2. 管理全局状态，如当前活动的工具和画布模式
 * 3. 整合各种服务(DrawingService, TextService, SelectionService, GridService, ViewportService)
 * 4. 协调工具栏、属性面板和画布之间的交互
 * 5. 处理全局键盘快捷键和上下文菜单
 * 6. 提供必要的回调函数给子组件
 * 7. 管理画布模式之间的切换逻辑
 * 8. 处理工具锁定功能，允许用户双击锁定工具
 * 9. 通过自定义事件处理工具完成信号
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import OptimizedCanvasBoard from './common/OptimizedCanvasBoard';
import ToolBar from './common/ToolBar';
import { CanvasMode, ContextMenuState, LineData, Point, CanvasContent, TextData, CardData, GroupData, SerializableLineData, ShapeType, GroupElement } from '../types';
import { DrawingService } from './services/DrawingService';
import { TextService } from './services/TextService';
import { SelectionService } from './services/SelectionService';
import { GridService } from './services/GridService';
import { ViewportService } from './services/ViewportService';
import { CardService } from './services/CardService';
import { GroupService } from './services/GroupService';
import { ImageService } from './services/ImageService';
import { KeyboardService, DEFAULT_SHORTCUTS } from './services/KeyboardService';
import { ResizeService } from './services/ResizeService';
import DrawingTool from './tools/DrawingTool';
import TextTool from './tools/TextTool';
import ShapeTool from './tools/ShapeTool';
import CardTool from './tools/CardTool';
import ImageTool from './tools/ImageTool';
import SelectionTool from './tools/SelectionTool';
import ContextMenu from './common/ContextMenu';

// 默认画布尺寸
const DEFAULT_CANVAS_WIDTH = 2000;
const DEFAULT_CANVAS_HEIGHT = 1500;

// 生成唯一ID
const generateId = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// 本地存储操作函数
const saveNoteToLocalStorage = (note: CanvasContent): void => {
  try {
    const notes = JSON.parse(localStorage.getItem('notes') || '{}');
    notes[note.noteId] = note;
    localStorage.setItem('notes', JSON.stringify(notes));
  } catch (error) {
    // 本地存储保存失败，但不影响主要功能
  }
};

const getNoteFromLocalStorage = (noteId: string): CanvasContent | null => {
  try {
    const notes = JSON.parse(localStorage.getItem('notes') || '{}');
    return notes[noteId] || null;
  } catch (error) {
    // 本地存储获取失败，但不影响主要功能
    return null;
  }
};

const getAllNotes = (): Record<string, CanvasContent> => {
  try {
    return JSON.parse(localStorage.getItem('notes') || '{}');
  } catch (error) {
    // 本地存储获取失败，但不影响主要功能
    return {};
  }
};

// 自定义事件名称常量
const TOOL_COMPLETE_EVENT = 'tool-operation-complete';

// 添加模块级别的变量来跟踪上次保存时间
let lastSaveMoveTime = 0;

const DrawingApp: React.FC = () => {
  // 创建一个空的ResizeService实例，保持类型兼容性
  const resizeService = useRef(new ResizeService()).current;
  // 状态
  const [canvasMode, setCanvasMode] = useState<CanvasMode>(CanvasMode.SELECT);
  const [previousMode, setPreviousMode] = useState<CanvasMode | null>(null);
  const [lockedTool, setLockedTool] = useState<CanvasMode | null>(null);
  const [contextMenuState, setContextMenuState] = useState<ContextMenuState>({ visible: false, x: 0, y: 0 });
  const [copiedLines, setCopiedLines] = useState<LineData[]>([]);
  const [copiedTexts, setCopiedTexts] = useState<TextData[]>([]);
  const [copiedCards, setCopiedCards] = useState<CardData[]>([]);
  const [forceUpdate, setForceUpdate] = useState<number>(0);
  const [currentNoteId, setCurrentNoteId] = useState<string>(generateId());
  const [currentNoteName, setCurrentNoteName] = useState<string>('Untitled Note');
  const [notes, setNotes] = useState<Record<string, CanvasContent>>(getAllNotes());
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [operationComplete, setOperationComplete] = useState<boolean>(false);
  // 添加工具面板位置状态
  const [panelPositions, setPanelPositions] = useState<{[key in CanvasMode]?: {top: number, left: number}}>({});
  const toolbarRef = useRef<HTMLDivElement>(null);

  // 创建各种服务实例
  const drawingServiceRef = useRef<DrawingService>(new DrawingService());
  const textServiceRef = useRef<TextService>(new TextService());
  const viewportServiceRef = useRef<ViewportService>(new ViewportService());
  const groupServiceRef = useRef<GroupService>(new GroupService());
  const selectionServiceRef = useRef<SelectionService>(new SelectionService(viewportServiceRef.current, groupServiceRef.current));
  const gridServiceRef = useRef<GridService>(new GridService());
  const keyboardServiceRef = useRef<KeyboardService>(new KeyboardService());
  const undoRedoServiceRef = useRef<any>({
    resetHistory: (lines: any, texts: any) => {} // 简单实现，保持接口一致
  });
  const cardServiceRef = useRef<CardService>(new CardService());
  const imageServiceRef = useRef<ImageService>(new ImageService(viewportServiceRef.current));
  
  // 获取当前的服务实例
  const drawingService = drawingServiceRef.current;
  const textService = textServiceRef.current;
  const selectionService = selectionServiceRef.current;
  const gridService = gridServiceRef.current;
  const viewportService = viewportServiceRef.current;
  const keyboardService = keyboardServiceRef.current;
  const undoRedoService = undoRedoServiceRef.current;
  const cardService = cardServiceRef.current;
  const groupService = groupServiceRef.current;
  const imageService = imageServiceRef.current;
  
  // 获取canvas元素引用
  const canvas = canvasRef.current;
  
  // 暴露服务实例到全局对象，用于测试
  React.useEffect(() => {
    // @ts-ignore
    window.__textService = textService;
    // @ts-ignore
    window.__drawingService = drawingService;
    // @ts-ignore
    window.__selectionService = selectionService;
    // @ts-ignore
    window.__groupService = groupService;
    
    return () => {
      // 清理全局对象
      // @ts-ignore
      delete window.__textService;
      // @ts-ignore
      delete window.__drawingService;
      // @ts-ignore
      delete window.__selectionService;
      // @ts-ignore
      delete window.__groupService;
    };
  }, [textService, drawingService, selectionService, groupService]);

  // 处理工具变更
  const handleToolChange = useCallback((mode: CanvasMode) => {
    // 如果当前有锁定的工具，但仍然允许切换到其他工具
    if (lockedTool) {
      // 如果选择的是锁定的工具，不改变锁定状态
      if (mode !== lockedTool) {
        // 如果选择了新工具，解除之前工具的锁定状态
        setLockedTool(null);
      }
      
      // 允许切换到任何工具
      setCanvasMode(mode);
      return;
    }
    
    // 没有锁定工具，正常切换
    setCanvasMode(mode);
    
    // 当切换到绘制或形状工具时，确保清除选择状态
    if (mode === CanvasMode.DRAW || mode === CanvasMode.SHAPE) {
      selectionService.clearSelection();
    }
    
    // 记录前一个模式
    if (mode !== CanvasMode.SELECT) {
      setPreviousMode(canvasMode);
    }
  }, [canvasMode, lockedTool, selectionService]);
  
  // 处理工具锁定
  const handleToolLock = useCallback((mode: CanvasMode) => {
    // 如果当前工具已锁定，则解锁
    if (lockedTool === mode) {
      setLockedTool(null);
      // 解锁后立即切换到默认选择工具
      setCanvasMode(CanvasMode.SELECT);
    } else {
      // 锁定新工具
      setLockedTool(mode);
      // 同时切换到被锁定的工具
      setCanvasMode(mode);
    }
  }, [lockedTool]);

  // 发送工具操作完成信号的辅助函数
  const sendToolCompleteSignal = useCallback(() => {
    // 创建自定义事件
    const event = new CustomEvent(TOOL_COMPLETE_EVENT);
    // 派发事件
    document.dispatchEvent(event);
  }, []);

  // 处理工具操作完成事件
  const handleToolComplete = useCallback(() => {
    // 如果当前工具已锁定，不进行自动切换
    if (lockedTool === canvasMode) {
      return;
    }
    
    // 切换回选择工具
    // 添加一个短延迟，确保所有UI更新和事件处理完成
    setTimeout(() => {
      setCanvasMode(CanvasMode.SELECT);
    }, 10);
  }, [canvasMode, lockedTool]);

  // 监听工具操作完成事件
  useEffect(() => {
    // 添加全局事件监听
    document.addEventListener(TOOL_COMPLETE_EVENT, handleToolComplete);
    
    // 清理函数
    return () => {
      document.removeEventListener(TOOL_COMPLETE_EVENT, handleToolComplete);
    };
  }, [handleToolComplete]);

  // 监听各种工具的操作完成状态
  useEffect(() => {
    // 只处理非选择工具且未锁定的情况
    if (canvasMode !== CanvasMode.SELECT && lockedTool !== canvasMode) {
      // 检查元素是否为工具栏或面板
      const isUIElement = (element: EventTarget | null): boolean => {
        if (!element) return false;
        
        // UI元素选择器列表
        const selectors = ['.toolbar', '.drawing-tool-panel', '.shape-tool-panel', '.text-tool-panel', '.card-tool-panel'];
        
        // 遍历DOM层次检查
        let current = element as Node | null;
        while (current) {
          if (current instanceof Element) {
            // 检查是否匹配任何选择器
            for (const selector of selectors) {
              if (current.matches(selector) || current.closest(selector)) {
                return true;
              }
            }
            
            // 检查类名是否包含关键词
            const classNames = current.className || '';
            if (typeof classNames === 'string' && 
                (classNames.includes('tool') || 
                 classNames.includes('panel') || 
                 classNames.includes('button'))) {
              return true;
            }
          }
          
          // 向上移动到父节点
          current = current.parentNode;
        }
        
        return false;
      };
      
      // 绘图和形状工具的处理
      if (canvasMode === CanvasMode.DRAW || canvasMode === CanvasMode.SHAPE) {
        // 设置监听ESC键
        const handleKeyDown = (e: KeyboardEvent) => {
          if (e.key === 'Escape') {
            // 不需要做任何操作，因为CanvasBoard中的ESC处理已经发送了工具完成事件
          }
        };
        
        document.addEventListener('keydown', handleKeyDown);
        
        // 清理函数
        return () => {
          document.removeEventListener('keydown', handleKeyDown);
        };
      } 
      // 文本工具的处理
      else if (canvasMode === CanvasMode.TEXT) {
        // 由TextService全权处理文本编辑，不再需要处理DOM元素
        // 文本工具状态监控
        const handleTextEditComplete = () => {
          sendToolCompleteSignal();
        };
        
        // 监听文本编辑完成事件
        document.addEventListener('text-edit-complete', handleTextEditComplete);
        
        return () => {
          document.removeEventListener('text-edit-complete', handleTextEditComplete);
        };
      }
      // 卡片工具的处理
      else if (canvasMode === CanvasMode.CARD) {
        // 取消自动发送工具完成信号的逻辑
        
        // 监听卡片编辑器完成事件
        const handleCardEditComplete = () => {
          // 寻找所有卡片编辑器
          const cardEditors = document.querySelectorAll('textarea[data-card-editor="true"]');
          if (cardEditors.length === 0) {
            // 没有找到卡片编辑器，可能是完成或取消了编辑
            setTimeout(sendToolCompleteSignal, 50);
          }
        };
        
        // 监听document上的blur事件，可能是从卡片编辑器切出
        document.addEventListener('focusout', (e) => {
          if (e.target && (e.target as HTMLElement).getAttribute('data-card-editor') === 'true') {
            // 延迟检查，给完成编辑的逻辑时间执行
            setTimeout(handleCardEditComplete, 100);
          }
        }, true);
        
        // 清理函数
        return () => {
          // 从document中移除事件监听
          document.removeEventListener('focusout', handleCardEditComplete, true);
        };
      }
    }
  }, [canvasMode, lockedTool, canvasRef, sendToolCompleteSignal]);

  // 保存画布内容
  const saveCanvasContent = (newName: string = '') => {
    const name = newName || currentNoteName;
    
    // 序列化线条数据，去除不可序列化的Path2D对象
    const linesToSave = drawingService.getLines().map(line => {
      // 创建一个新对象，排除path属性
      const { path, ...lineWithoutPath } = line;
      return lineWithoutPath as SerializableLineData;
    });
    
    // 获取当前视口状态（缩放和平移信息）
    const viewportState = viewportService.getViewportState();
    
    const currentContent: CanvasContent = {
      lines: linesToSave,
      texts: textService.getTexts(),
      cards: cardService.getCards(),
      images: imageService.getImages(), // 添加图片数据
      groups: groupService.getGroups(),
      noteId: currentNoteId,
      width: DEFAULT_CANVAS_WIDTH,
      height: DEFAULT_CANVAS_HEIGHT,
      name: name,
      createdAt: Date.now(),
      viewport: viewportState // 保存视口状态
    };
    
    saveNoteToLocalStorage(currentContent);
    
    // 更新内存中的笔记列表
    setNotes(prevNotes => ({
      ...prevNotes,
      [currentNoteId]: currentContent
    }));
    
    if (newName) {
      setCurrentNoteName(newName);
    }
  };
  
  // 在工具操作完成时自动保存画布内容
  useEffect(() => {
    const handleToolOperationComplete = () => {
      // 延迟一点执行保存，确保所有状态已更新
      setTimeout(() => {
        saveCanvasContent();
      }, 100);
    };
    
    // 添加全局事件监听
    document.addEventListener(TOOL_COMPLETE_EVENT, handleToolOperationComplete);
    
    // 清理函数
    return () => {
      document.removeEventListener(TOOL_COMPLETE_EVENT, handleToolOperationComplete);
    };
  }, [saveCanvasContent]);
  
  // 加载笔记
  const loadNote = (noteId: string) => {
    const note = getNoteFromLocalStorage(noteId);
    if (note) {
      // 加载线条数据
      if (note.lines) {
        // 为每个线条重新创建Path2D对象
        const reconstructedLines = note.lines.map(line => {
          const newPath = new Path2D();
          
          // 如果有点数据，重建路径
          if (line.points && line.points.length > 0) {
            // 移动到第一个点
            newPath.moveTo(line.points[0].x, line.points[0].y);
            
            // 连接其余所有点
            for (let i = 1; i < line.points.length; i++) {
              newPath.lineTo(line.points[i].x, line.points[i].y);
            }
          }
          
          // 返回重建的线条对象，从SerializableLineData转换为LineData
          return {
            ...line,
            path: newPath
          } as LineData;
        });
        
        drawingService.setLines(reconstructedLines);
      } else {
        drawingService.setLines([]);
      }
      
      // 加载文本数据
      textService.setTexts(note.texts || []);
      
      // 加载卡片数据
      cardService.setCards(note.cards || []);
      
      // 加载图片数据
      imageService.setImages(note.images || []);
      
      // 加载组合数据
      groupService.setGroups(note.groups || []);
      
      // 恢复视口状态（缩放和平移）
      if (note.viewport) {
        viewportService.setViewportState(
          note.viewport.scale,
          note.viewport.offsetX,
          note.viewport.offsetY
        );
      } else {
        // 如果没有保存的视口状态，重置为默认状态
        viewportService.reset();
      }
      
      // 更新当前笔记ID和名称
      setCurrentNoteId(noteId);
      setCurrentNoteName(note.name || 'Untitled Note');
      
      // 重置选择状态
      selectionService.clearSelection();
      
      // 重置历史记录
      undoRedoService.resetHistory(note.lines, note.texts);
      
      // 触发重绘
      setForceUpdate(prev => prev + 1);
    }
  };

  // 新建笔记
  const createNewNote = () => {
    // 生成新笔记ID
    const newNoteId = generateId();
    
    // 清空画布内容
    drawingService.setLines([]);
    textService.setTexts([]);
    cardService.setCards([]);
    imageService.setImages([]);
    
    // 重置组合状态
    groupService.resetGroups();
    
    // 重置历史记录
    undoRedoService.resetHistory([], []);
    
    // 更新笔记ID和名称
    setCurrentNoteId(newNoteId);
    setCurrentNoteName('Untitled Note');
    
    // 保存新笔记到本地存储
    saveCanvasContent();
    
    // 强制重新渲染
    setForceUpdate(prev => prev + 1);
  };

  // 初始化键盘快捷键
  useEffect(() => {
    // 工具快捷键注册
    Object.entries(DEFAULT_SHORTCUTS).forEach(([key, value]) => {
      if (typeof value === 'string' && value in CanvasMode) {
        const mode = value as CanvasMode;
        keyboardService.registerToolShortcut(
          key,
          () => handleToolChange(mode),
          () => handleToolLock(mode)
        );
      } else if (typeof value === 'string') {
        // 其他操作快捷键
        switch (value) {
          case 'undo':
            keyboardService.registerShortcut(key, () => {
              // 实现撤销逻辑
            });
            break;
          case 'redo':
            keyboardService.registerShortcut(key, () => {
              // 实现重做逻辑
            });
            break;
          case 'copy':
            keyboardService.registerShortcut(key, () => {
              // 获取选中的所有元素
              const selectedLineIndices = selectionService.getSelectedIndices();
              const selectedTextIndices = selectionService.getSelectedTextIndices();
              const selectedCardIndices = selectionService.getSelectedCardIndices();
              
              // 复制线条
              if (selectedLineIndices.length > 0) {
                const selectedLines = selectedLineIndices.map(index => drawingService.getLines()[index]).filter(Boolean);
                setCopiedLines(selectedLines);
              } else {
                setCopiedLines([]);
              }
              
              // 复制文本
              if (selectedTextIndices.length > 0) {
                const selectedTexts = selectedTextIndices.map(index => textService.getTexts()[index]).filter(Boolean);
                setCopiedTexts(selectedTexts);
              } else {
                setCopiedTexts([]);
              }
              
              // 复制卡片
              if (selectedCardIndices.length > 0) {
                const selectedCards = selectedCardIndices.map(index => cardService.getCards()[index]).filter(Boolean);
                setCopiedCards(selectedCards);
              } else {
                setCopiedCards([]);
              }
              
              // 存储选中元素的组合信息
              const groupedElements: Record<string, GroupElement[]> = {};
              
              // 查找线条的组合
              selectedLineIndices.forEach((index, arrayIndex) => {
                const groupId = groupService.isElementInAnyGroup('line', index);
                if (groupId) {
                  if (!groupedElements[groupId]) {
                    groupedElements[groupId] = [];
                  }
                  groupedElements[groupId].push({ type: 'line', index: arrayIndex });
                }
              });
              
              // 查找文本的组合
              selectedTextIndices.forEach((index, arrayIndex) => {
                const groupId = groupService.isElementInAnyGroup('text', index);
                if (groupId) {
                  if (!groupedElements[groupId]) {
                    groupedElements[groupId] = [];
                  }
                  groupedElements[groupId].push({ type: 'text', index: arrayIndex });
                }
              });
              
              // 查找卡片的组合
              selectedCardIndices.forEach((index, arrayIndex) => {
                const groupId = groupService.isElementInAnyGroup('card', index);
                if (groupId) {
                  if (!groupedElements[groupId]) {
                    groupedElements[groupId] = [];
                  }
                  groupedElements[groupId].push({ type: 'card', index: arrayIndex });
                }
              });
              
              // 将组合信息存储在window对象中，以便在粘贴时使用
              // @ts-ignore
              window.__copiedGroups = groupedElements;
            });
            break;
          case 'paste':
            keyboardService.registerShortcut(key, () => {
              // 如果有任何复制的内容，执行粘贴操作
              if (copiedLines.length > 0 || copiedTexts.length > 0 || copiedCards.length > 0) {
                // 获取当前视口中心作为粘贴位置
                const canvasElement = canvasRef.current;
                if (canvasElement) {
                  const rect = canvasElement.getBoundingClientRect();
                  const centerX = rect.left + rect.width / 2;
                  const centerY = rect.top + rect.height / 2;
                  
                  // 创建临时上下文菜单状态，用于存储粘贴相关信息
                  // 这样我们可以在关闭后才清空上下文菜单状态
                  const tempContextMenuState: ContextMenuState = {
                    visible: true,
                    x: centerX,
                    y: centerY,
                    type: 'empty'
                  };
                  
                  // 设置上下文菜单状态，以便在渲染时处理粘贴
                  setContextMenuState(tempContextMenuState);
                  
                  // 立即关闭上下文菜单状态，这样仅执行粘贴操作
                  setTimeout(() => {
                    setContextMenuState(prev => ({ ...prev, visible: false }));
                  }, 0);
                  
                  // 在粘贴完成后进行组合关系恢复
                  setTimeout(() => {
                    // 如果没有组合信息，则不进行后续操作
                    // @ts-ignore
                    const copiedGroups = window.__copiedGroups || {};
                    if (Object.keys(copiedGroups).length === 0) return;
                    
                    // 获取当前画布上所有元素的数量
                    const currentLines = drawingService.getLines();
                    const currentTexts = textService.getTexts();
                    const currentCards = cardService.getCards();
                    
                    // 创建索引映射
                    const indexMapping: Record<string, {oldIndex: number, newIndex: number}[]> = {
                      'line': [],
                      'text': [],
                      'card': []
                    };
                    
                    // 计算线条的索引映射
                    const lineStartIndex = currentLines.length - copiedLines.length;
                    for (let i = 0; i < copiedLines.length; i++) {
                      indexMapping['line'].push({
                        oldIndex: i, 
                        newIndex: lineStartIndex + i
                      });
                    }
                    
                    // 计算文本的索引映射
                    const textStartIndex = currentTexts.length - copiedTexts.length;
                    for (let i = 0; i < copiedTexts.length; i++) {
                      indexMapping['text'].push({
                        oldIndex: i,
                        newIndex: textStartIndex + i
                      });
                    }
                    
                    // 计算卡片的索引映射
                    const cardStartIndex = currentCards.length - copiedCards.length;
                    for (let i = 0; i < copiedCards.length; i++) {
                      indexMapping['card'].push({
                        oldIndex: i,
                        newIndex: cardStartIndex + i
                      });
                    }
                    
                    // 为每个组重新创建组合关系
                    Object.entries(copiedGroups).forEach(([_, elements]) => {
                      // 创建新的组元素集合，将原始索引映射到新索引
                      const newGroupElements: GroupElement[] = [];
                      const groupElements = elements as GroupElement[];
                      
                      groupElements.forEach((element) => {
                        const { type, index: oldIndex } = element;
                        // 查找对应的新索引
                        const mapping = indexMapping[type].find(m => m.oldIndex === oldIndex);
                        if (mapping) {
                          newGroupElements.push({
                            type,
                            index: mapping.newIndex
                          });
                        }
                      });
                      
                      // 只有当有至少两个元素时才创建组
                      if (newGroupElements.length >= 2) {
                        groupService.createGroup(newGroupElements);
                      }
                    });
                  }, 10); // 短暂延迟，确保粘贴操作已完成
                }
              }
            });
            break;
          case 'cut':
            keyboardService.registerShortcut(key, () => {
              // 获取选中的所有元素
              const selectedLineIndices = selectionService.getSelectedIndices();
              const selectedTextIndices = selectionService.getSelectedTextIndices();
              const selectedCardIndices = selectionService.getSelectedCardIndices();
              
              // 复制线条
              if (selectedLineIndices.length > 0) {
                const selectedLines = selectedLineIndices.map(index => drawingService.getLines()[index]).filter(Boolean);
                setCopiedLines(selectedLines);
              } else {
                setCopiedLines([]);
              }
              
              // 复制文本
              if (selectedTextIndices.length > 0) {
                const selectedTexts = selectedTextIndices.map(index => textService.getTexts()[index]).filter(Boolean);
                setCopiedTexts(selectedTexts);
              } else {
                setCopiedTexts([]);
              }
              
              // 复制卡片
              if (selectedCardIndices.length > 0) {
                const selectedCards = selectedCardIndices.map(index => cardService.getCards()[index]).filter(Boolean);
                setCopiedCards(selectedCards);
              } else {
                setCopiedCards([]);
              }
              
              // 删除选中内容
              selectionService.deleteSelectedContent(drawingService, textService, cardService);
            });
            break;
          case 'selectAll':
            keyboardService.registerShortcut(key, () => {
              // 实现全选逻辑
            });
            break;
          case 'delete':
            keyboardService.registerShortcut(key, () => {
              // 实现删除选中内容逻辑
              if (selectionService.getSelectedIndices().length > 0) {
                // 删除选中内容
                selectionService.deleteSelectedContent(drawingService, textService, cardService);
              }
            });
            break;
          case 'duplicate':
            keyboardService.registerShortcut(key, () => {
              // 实现复制所选逻辑
            });
            break;
          case 'resetZoom':
            keyboardService.registerShortcut(key, () => {
              // 实现重置缩放逻辑
            });
            break;
          case 'zoomIn':
            keyboardService.registerShortcut(key, () => {
              // 实现放大逻辑
            });
            break;
          case 'zoomOut':
            keyboardService.registerShortcut(key, () => {
              // 实现缩小逻辑
            });
            break;
          case 'tempHandTool':
            keyboardService.registerShortcut(key, () => {
              // 实现临时手型工具逻辑
            });
            break;
          case 'save':
            keyboardService.registerShortcut(key, () => {
              // 实现保存逻辑
              saveCanvasContent();
            });
            break;
          case 'new':
            keyboardService.registerShortcut(key, () => {
              // 实现新建逻辑
              createNewNote();
            });
            break;
          case 'open':
            keyboardService.registerShortcut(key, () => {
              // 实现打开逻辑
            });
            break;
          case 'cancelOperation':
            keyboardService.registerShortcut(key, () => {
              // 实现取消当前操作逻辑
            });
            break;
          case 'toggleHelp':
            keyboardService.registerShortcut(key, () => {
              // 实现切换帮助面板逻辑
            });
            break;
        }
      }
    });

    // 在文本编辑模式下禁用快捷键
    const disableShortcutsInTextMode = () => {
      // 检查是否有文本正在编辑
      const isTextEditing = textService.getTexts().some(text => text.isEditing);
      
      if (canvasMode === CanvasMode.TEXT && isTextEditing) {
        keyboardService.disable();
      } else {
        keyboardService.enable();
      }
    };

    // 监听文本编辑状态变化
    const textEditingObserver = () => {
      disableShortcutsInTextMode();
    };

    // 初始状态检查
    disableShortcutsInTextMode();

    // 清理函数
    return () => {
      keyboardService.destroy();
    };
  }, [
    keyboardService,
    handleToolChange,
    handleToolLock,
    canvasMode,
    textService,
    selectionService,
    drawingService,
    copiedLines,
    setCopiedLines,
    saveCanvasContent,
    createNewNote
  ]);

  // 添加新的useEffect钩子，用于在组件加载时恢复上次的笔记
  useEffect(() => {
    // 从localStorage获取所有笔记
    const savedNotes = getAllNotes();
    
    // 如果有保存的笔记
    if (Object.keys(savedNotes).length > 0) {
      // 找出最近修改的笔记
      const sortedNoteIds = Object.keys(savedNotes).sort((a, b) => {
        return (savedNotes[b].createdAt || 0) - (savedNotes[a].createdAt || 0);
      });
      
      if (sortedNoteIds.length > 0) {
        // 加载最近的笔记
        const mostRecentNoteId = sortedNoteIds[0];
        loadNote(mostRecentNoteId);
      }
    } else {
      // 如果没有保存的笔记，创建一个新笔记
      createNewNote();
    }
  }, []); // 空依赖数组确保只在组件首次渲染时执行一次

  // 完成线条选择
  const completeLineSelection = useCallback((addToExisting: boolean) => {
    // 使用SelectionService的completeSelection方法
    selectionService.completeSelection(
      drawingService.getLines(), 
      addToExisting,
      textService.getTexts(),
      cardService.getCards(),
      imageService.getImages()
    );
  }, [selectionService, drawingService, textService, cardService, imageService]);
  
  // 辅助函数 - 移动选中的内容
  const moveSelectedContent = useCallback((point: Point) => {
    if (!selectionService.isContentMoving() || canvasMode !== CanvasMode.SELECT) return;
    
    const selectedIndices = selectionService.getSelectedIndices();
    const selectedTextIndices = selectionService.getSelectedTextIndices();
    const selectedCardIndices = selectionService.getSelectedCardIndices();
    const selectedImageIndices = selectionService.getSelectedImageIndices();
    const lines = drawingService.getLines();
    const texts = textService.getTexts();
    const cards = cardService.getCards();
    const images = imageService.getImages();
    const lastMovePoint = (selectionService as any).lastMovePoint;
    
    if (!lastMovePoint) return;
    
    const deltaX = point.x - lastMovePoint.x;
    const deltaY = point.y - lastMovePoint.y;
    
    // 性能优化：只有在移动距离超过阈值时才更新路径
    // 这可以减少不必要的Path2D创建，提高拖拽性能
    const shouldUpdatePaths = Math.abs(deltaX) > 0.5 || Math.abs(deltaY) > 0.5;
    
    // 更新选中的线条
    // 设计理念：移动操作不区分普通线条和形状
    // 所有绘图元素都只是点的集合，移动时只需移动这些点
    // 这种统一处理极大简化了代码，避免了复杂形状移动时的问题
    selectedIndices.forEach(index => {
      // 确保索引在有效范围内
      if (index < lines.length) {
        const line = lines[index];
        
        // 更新线条中的每个点
        line.points.forEach(p => {
          p.x += deltaX;
          p.y += deltaY;
        });
        
        // 性能优化：只有在需要时才重新创建路径
        if (shouldUpdatePaths) {
          // 重新创建路径
          const newPath = new Path2D();
          if (line.points.length > 0) {
            newPath.moveTo(line.points[0].x, line.points[0].y);
            for (let i = 1; i < line.points.length; i++) {
              newPath.lineTo(line.points[i].x, line.points[i].y);
            }
          }
          
          // 更新线条路径
          line.path = newPath;
        }
      }
    });
    
    // 更新选中的文本
    selectedTextIndices.forEach(index => {
      if (index < texts.length) {
        const text = texts[index];
        
        // 更新文本位置
        text.baseX += deltaX;
        text.baseY += deltaY;
        
        // 更新文本框坐标（如果存在）
        if (text.boxX !== undefined && text.boxY !== undefined) {
          text.boxX += deltaX;
          text.boxY += deltaY;
        }
      }
    });
    
    // 触发文本位置更新事件，用于更新注释编辑框的连接线
    if (selectedTextIndices.length > 0) {
      const event = new CustomEvent('text-position-updated');
      document.dispatchEvent(event);
    }
    
    // 更新选中的卡片
    selectedCardIndices.forEach(index => {
      if (index < cards.length) {
        const card = cards[index];
        
        // 更新卡片位置
        card.x += deltaX;
        card.y += deltaY;
      }
    });
    
    // 更新选中的图片
    selectedImageIndices.forEach(index => {
      if (index < images.length) {
        const image = images[index];
        
        // 更新图片位置
        image.x += deltaX;
        image.y += deltaY;
      }
    });
    
    // 更新最后移动点
    selectionService.moveSelectedContent(point);
    
    // 性能优化：使用节流方式更新渲染
    // 在拖拽过程中，不需要每次移动都触发forceUpdate
    // 只在移动距离超过阈值时才触发
    if (shouldUpdatePaths) {
      setForceUpdate(prev => prev + 1);
    }
  }, [canvasMode, drawingService, textService, cardService, imageService, selectionService]);

  // 处理停止移动选中内容（在移动结束后添加处理）
  const handleStopMoving = useCallback(() => {
    // 确保在停止移动后强制刷新渲染，从而更新选择框位置
    setForceUpdate(prev => prev + 1);
    
    // 延迟触发保存事件，确保元素位置和视图更新完成后再保存
    setTimeout(() => {
      // 触发工具操作完成事件，自动保存当前内容状态
      document.dispatchEvent(new CustomEvent(TOOL_COMPLETE_EVENT));
    }, 300);
  }, []);

  // 计算工具面板位置
  useEffect(() => {
    // 获取工具栏和按钮位置
    const updatePanelPositions = () => {
      if (toolbarRef.current) {
        const toolbarRect = toolbarRef.current.getBoundingClientRect();
        const toolButtons = toolbarRef.current.querySelectorAll('button');
        
        const newPositions: {[key in CanvasMode]?: {top: number, left: number}} = {};
        
        // 遍历工具栏按钮，计算对应面板的位置
        toolButtons.forEach((button, index) => {
          const buttonRect = button.getBoundingClientRect();
          const modeIndex = [
            CanvasMode.SELECT,
            CanvasMode.DRAW, 
            CanvasMode.TEXT, 
            CanvasMode.SHAPE, 
            CanvasMode.CARD, 
            CanvasMode.IMAGE
          ][index];
          
          newPositions[modeIndex] = {
            top: buttonRect.top - 10, // 稍微往上一点
            left: toolbarRect.right + 10 // 工具栏右侧偏移10像素
          };
        });
        
        setPanelPositions(newPositions);
      }
    };
    
    // 初始化时计算位置
    setTimeout(updatePanelPositions, 100);
    
    // 监听窗口大小变化事件，更新面板位置
    window.addEventListener('resize', updatePanelPositions);
    
    // 监听滚动事件，更新面板位置
    window.addEventListener('scroll', updatePanelPositions);
    
    // 清理函数
    return () => {
      window.removeEventListener('resize', updatePanelPositions);
      window.removeEventListener('scroll', updatePanelPositions);
    };
  }, []);

  return (
    <div className="drawing-app" style={{ width: '100%', height: '100vh', position: 'relative' }}>
      <ToolBar 
        onToolChange={handleToolChange} 
        canvasMode={canvasMode}
        onToolLock={handleToolLock}
        lockedTool={lockedTool}
        ref={toolbarRef}
      />
      
      <OptimizedCanvasBoard 
        canvasMode={canvasMode}
        drawingService={drawingService}
        textService={textService}
        resizeService={resizeService}
        selectionService={selectionService}
        gridService={gridService}
        viewportService={viewportService}
        cardService={cardService}
        groupService={groupService}
        imageService={imageService}
        setContextMenuState={setContextMenuState}
        copiedLines={copiedLines}
        setCopiedLines={setCopiedLines}
        completeLineSelection={completeLineSelection}
        moveSelectedContent={moveSelectedContent}
        handleStopMoving={handleStopMoving}
      />
      
      {canvasMode === CanvasMode.SELECT && (
        <SelectionTool 
          isActive={canvasMode === CanvasMode.SELECT}
          position={panelPositions[CanvasMode.SELECT]}
        />
      )}
      
      {canvasMode === CanvasMode.DRAW && (
        <DrawingTool 
          drawingService={drawingService}
          isActive={canvasMode === CanvasMode.DRAW}
          position={panelPositions[CanvasMode.DRAW]}
        />
      )}
      
      {canvasMode === CanvasMode.TEXT && (
        <TextTool 
          textService={textService}
          isActive={canvasMode === CanvasMode.TEXT}
          position={panelPositions[CanvasMode.TEXT]}
        />
      )}
      
      {canvasMode === CanvasMode.SHAPE && (
        <ShapeTool 
          drawingService={drawingService}
          isActive={canvasMode === CanvasMode.SHAPE}
          position={panelPositions[CanvasMode.SHAPE]}
        />
      )}
      
      {canvasMode === CanvasMode.CARD && (
        <CardTool 
          cardService={cardService}
          isActive={canvasMode === CanvasMode.CARD}
          position={panelPositions[CanvasMode.CARD]}
        />
      )}
      
      {canvasMode === CanvasMode.IMAGE && (
        <ImageTool 
          imageService={imageService}
          isActive={canvasMode === CanvasMode.IMAGE}
          position={panelPositions[CanvasMode.IMAGE]}
        />
      )}
      
      {contextMenuState.visible && (
        <ContextMenu
          x={contextMenuState.x}
          y={contextMenuState.y}
          onClose={() => setContextMenuState({ visible: false, x: 0, y: 0 })}
          selectionService={selectionService}
          drawingService={drawingService}
          textService={textService}
          cardService={cardService}
          groupService={groupService}
          imageService={imageService}
          setCopiedLines={setCopiedLines}
          copiedLines={copiedLines}
          setCopiedTexts={setCopiedTexts}
          copiedTexts={copiedTexts}
          setCopiedCards={setCopiedCards}
          copiedCards={copiedCards}
          type={contextMenuState.type}
          textIndex={contextMenuState.textIndex}
          lineIndex={contextMenuState.lineIndex}
          cardIndex={contextMenuState.cardIndex}
          groupId={contextMenuState.groupId}
        />
      )}
    </div>
  );
};

export default DrawingApp;